import { parseIntegerInput } from "@/lib/parse-number";
import {
  ceilDiv,
  type DayNumber,
  daysMatching,
  isoFromDay,
  parseIsoDay,
} from "@/lib/tool-kit/study/dates";

/**
 * The words-per-day calculator: a daily quota, a dated schedule, and a progress log.
 *
 * The quota is the remaining words over the remaining writing days, rounded up — a
 * quota rounded down misses the target by a few words on the last day. The schedule
 * uses cumulative targets (by the end of writing day *i* of *n*, `ceil(i × remaining ÷ n)`
 * words), so the daily figures differ by at most one word and the last day lands on the
 * target exactly: 50,000 words in 30 days is 1,667 a day, with one 1,666 every third day.
 */

export interface WritingDay {
  day: DayNumber;
  /** Words to write on this day. */
  words: number;
  /** The running total you should have reached by the end of the day. */
  total: number;
}

export interface WritingPlan {
  /** The headline quota: remaining words ÷ writing days, rounded up. */
  perDay: number;
  remaining: number;
  rows: WritingDay[];
}

export function planWriting(
  target: number,
  written: number,
  days: readonly DayNumber[]
): WritingPlan {
  const remaining = Math.max(0, target - Math.max(0, written));
  const count = days.length;
  if (count === 0 || remaining === 0) {
    return { perDay: 0, remaining, rows: [] };
  }
  const start = Math.max(0, written);
  const rows: WritingDay[] = [];
  let done = 0;
  for (const [index, day] of days.entries()) {
    const reached = ceilDiv((index + 1) * remaining, count);
    rows.push({ day, words: reached - done, total: start + reached });
    done = reached;
  }
  return { perDay: ceilDiv(remaining, count), remaining, rows };
}

/** One progress check-in: your total word count on a date. */
export interface LogEntry {
  /** `YYYY-MM-DD`. */
  date: string;
  total: number;
}

/** Add or replace the entry for `date`, keeping the log in date order. */
export function upsertLogEntry(
  log: readonly LogEntry[],
  date: string,
  total: number
): LogEntry[] {
  return [...log.filter((entry) => entry.date !== date), { date, total }].sort(
    (a, b) => a.date.localeCompare(b.date)
  );
}

export interface LogRow extends LogEntry {
  /** Words added since the previous check-in; null for the first. */
  change: number | null;
}

export function logWithChanges(log: readonly LogEntry[]): LogRow[] {
  return log.map((entry, index) => ({
    ...entry,
    change: index === 0 ? null : entry.total - log[index - 1].total,
  }));
}

/**
 * What the widget keeps in localStorage.
 *
 * Inputs are stored as the strings the fields hold, so a half-typed value survives a
 * reload exactly as it was typed.
 */
export interface SavedWritingGoal {
  target: string;
  written: string;
  start: string;
  deadline: string;
  /** Monday-first toggle positions that are on. */
  weekdays: number[];
  log: LogEntry[];
}

const ISO_DAY = /^\d{4}-\d{2}-\d{2}$/;

function isString(value: unknown): value is string {
  return typeof value === "string";
}

function parseLog(raw: unknown): LogEntry[] {
  if (!Array.isArray(raw)) {
    return [];
  }
  const entries: LogEntry[] = [];
  for (const item of raw) {
    if (
      item &&
      typeof item === "object" &&
      isString((item as LogEntry).date) &&
      ISO_DAY.test((item as LogEntry).date) &&
      Number.isFinite((item as LogEntry).total)
    ) {
      entries.push({
        date: (item as LogEntry).date,
        total: Math.max(0, Math.round((item as LogEntry).total)),
      });
    }
  }
  return entries.sort((a, b) => a.date.localeCompare(b.date));
}

/**
 * Validate what came back out of storage.
 *
 * Storage is outside the page's control — another tab, an old version of this widget, a
 * browser extension — so anything that is not the expected shape is discarded rather
 * than trusted.
 */
export function parseSavedWritingGoal(raw: unknown): SavedWritingGoal | null {
  if (!raw || typeof raw !== "object") {
    return null;
  }
  const record = raw as Record<string, unknown>;
  const fields = ["target", "written", "start", "deadline"] as const;
  if (!fields.every((field) => isString(record[field]))) {
    return null;
  }
  const weekdays = Array.isArray(record.weekdays)
    ? record.weekdays.filter(
        (value): value is number =>
          Number.isInteger(value) && value >= 0 && value <= 6
      )
    : [];
  return {
    target: record.target as string,
    written: record.written as string,
    start: record.start as string,
    deadline: record.deadline as string,
    weekdays: [...new Set(weekdays)].sort((a, b) => a - b),
    log: parseLog(record.log),
  };
}

/** Why there is no schedule yet: a prompt, or one of three mistakes. */
export type WritingProblem =
  | "enterTarget"
  | "deadlineBeforeStart"
  | "tooLong"
  | "noWritingDays";

/**
 * The writing days between start and deadline, or what stops there being any.
 *
 * @param weekdays `weekdayOf` numbers of the days you write.
 */
export function writingDays(
  goal: { target: string; start: string; deadline: string },
  weekdays: ReadonlySet<number>,
  maxDays: number
): { days: DayNumber[]; problem: WritingProblem | null } {
  const start = parseIsoDay(goal.start);
  const deadline = parseIsoDay(goal.deadline);
  const target = parseIntegerInput(goal.target);
  if (start === null || deadline === null || target === null || target <= 0) {
    return { days: [], problem: "enterTarget" };
  }
  if (deadline < start) {
    return { days: [], problem: "deadlineBeforeStart" };
  }
  if (deadline - start > maxDays) {
    return { days: [], problem: "tooLong" };
  }
  const days = daysMatching(start, deadline, weekdays);
  return { days, problem: days.length === 0 ? "noWritingDays" : null };
}

/**
 * A saved goal brought up to date: a start date that has already passed moves to
 * today, because a schedule counting days that are over would ask for too few words.
 */
export function restoreWritingGoal(
  saved: SavedWritingGoal,
  today: DayNumber
): SavedWritingGoal {
  const start = parseIsoDay(saved.start);
  return { ...saved, start: isoFromDay(Math.max(start ?? today, today)) };
}
